import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import type { AuctionFormInput } from '../schemas/auction-form-schema';
import { DEFAULT_INCREMENTS, MAX_INCREMENT_TIERS } from '../schemas/auction-rules';

/**
 * The increment table as "from / step" rows: from a current price upwards, the next bid is at
 * least that price plus the step. The first row starts at 0 and `from` only goes up.
 */
export function IncrementTableEditor({
  currency,
  disabled = false,
}: {
  currency: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation(['auctions', 'common']);
  const form = useFormContext<AuctionFormInput>();
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'incrementTable',
  });
  const tableError =
    form.formState.errors.incrementTable?.root?.message ??
    form.formState.errors.incrementTable?.message;

  return (
    <fieldset className="flex flex-col gap-2" data-tour="auctions.form.increments">
      <legend className="text-sm font-medium">{t('auctions:fields.increments')}</legend>
      <p className="text-muted-foreground text-xs">{t('auctions:fields.incrementsHint')}</p>
      <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
        {fields.map((row, index) => (
          <div key={row.id} className="contents">
            <FormField
              control={form.control}
              name={`incrementTable.${index}.from`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className={index === 0 ? undefined : 'sr-only'}>
                    {t('auctions:fields.tierFrom', { index: index + 1 })}
                  </FormLabel>
                  <FormControl>
                    <MoneyInput
                      currency={currency}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      disabled={disabled}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`incrementTable.${index}.step`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className={index === 0 ? undefined : 'sr-only'}>
                    {t('auctions:fields.tierStep', { index: index + 1 })}
                  </FormLabel>
                  <FormControl>
                    <MoneyInput
                      currency={currency}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      disabled={disabled}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mb-0.5"
              disabled={disabled || fields.length <= 1}
              onClick={() => remove(index)}
              aria-label={t('auctions:fields.removeTier', { index: index + 1 })}
            >
              <Trash2 aria-hidden />
            </Button>
          </div>
        ))}
      </div>
      {tableError ? (
        <p role="alert" className="text-destructive text-sm">
          {tableError}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || fields.length >= MAX_INCREMENT_TIERS}
          onClick={() => {
            const last =
              fields.length > 0 ? form.getValues(`incrementTable.${fields.length - 1}`) : undefined;
            append({
              from: last?.from
                ? { amount: last.from.amount + 100_000, currency }
                : { amount: 0, currency },
              step: last?.step ?? null,
            });
          }}
        >
          <Plus aria-hidden />
          {t('auctions:fields.addTier')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() =>
            replace(
              DEFAULT_INCREMENTS.map((tier) => ({
                from: { amount: tier.fromCents, currency },
                step: { amount: tier.incrementCents, currency },
              })),
            )
          }
        >
          <RotateCcw aria-hidden />
          {t('auctions:fields.resetTiers')}
        </Button>
      </div>
    </fieldset>
  );
}
